import { getFilterObject } from '@/common';
import { FiltersType, OfferType, SearchListFilters, Unit, UnitsMultilevelList } from '@/types';
import { pushFilterInteraction } from './dataLayer';
import type { ObjectSearchSource } from './searchPayload';

/**
 * GA4 `filter_interaction` dataLayer helpers for the housing list.
 * Builds rows per filter group (German `filter_group` keys from the spec), deduplicates pushes
 * via `sessionStorage` (in-memory fallback when unavailable), and skips unchanged groups.
 */

const FILTER_INTERACTION_STORAGE_PREFIX = 'analytics:filter_interaction:';

const OPTIONAL_FILTER_GROUPS = [
  'immobilienart',
  'land',
  'ausstattung',
  'price_min',
  'price_max',
] as const;

const filterInteractionMemoryFallback = new Map<string, string>();

function getFilterInteractionStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function readFilterInteractionSnapshot(storageKey: string): string | null {
  const s = getFilterInteractionStorage();
  if (s) return s.getItem(storageKey);
  return filterInteractionMemoryFallback.get(storageKey) ?? null;
}

function writeFilterInteractionSnapshot(storageKey: string, value: string) {
  const s = getFilterInteractionStorage();
  if (s) s.setItem(storageKey, value);
  else filterInteractionMemoryFallback.set(storageKey, value);
}

function removeFilterInteractionSnapshot(storageKey: string) {
  const s = getFilterInteractionStorage();
  if (s) s.removeItem(storageKey);
  else filterInteractionMemoryFallback.delete(storageKey);
}

function readStoredValues(storageKey: string): Set<string> {
  const raw = readFilterInteractionSnapshot(storageKey);
  if (!raw) return new Set();
  try {
    const parsed = JSON.parse(raw) as string[];
    return new Set(parsed);
  } catch {
    return new Set();
  }
}

/**
 * Removes a trailing parenthetical suffix from UI labels (e.g. counts like `(184)`).
 */
export function stripFilterLabelTitle(raw: string): string {
  return raw.replace(/\s*\([^)]*\)\s*$/, '').trim();
}

function offerTypeLabel(offer: OfferType): 'Kauf' | 'Miete' {
  return offer === OfferType.SELL ? 'Kauf' : 'Miete';
}

function findUnitTitle(unitList: UnitsMultilevelList, code: string): string | null {
  type Flat = Unit & { children?: Unit[] };
  const walk = (list: Flat[], c: string): string | null => {
    for (const item of list) {
      if (item.code === c) return item.title;
      if (item.children?.length) {
        const found = walk(item.children as Flat[], c);
        if (found) return found;
      }
    }
    return null;
  };
  return walk(unitList as unknown as Flat[], code);
}

/**
 * Returns a map of filter group -> individual values for the current filter state.
 * Optional groups are omitted when empty.
 */
export function buildFilterGroupValues(
  filters: FiltersType,
  preparedFilters: FiltersType,
  unitList: UnitsMultilevelList | null | undefined,
): Map<string, string[]> {
  const result = new Map<string, string[]>();

  result.set('angebotstyp', [offerTypeLabel(filters.offerTypeValue)]);

  const catValues = filters.categoriesValue
    .map((c) => stripFilterLabelTitle(c.title))
    .filter(Boolean);
  if (catValues.length) result.set('immobilienart', catValues);

  if (unitList?.length && filters.unitListValue.length) {
    const landValues = [
      ...new Set(
        filters.unitListValue
          .map((code) => findUnitTitle(unitList, code))
          .filter((x): x is string => !!x)
          .map(stripFilterLabelTitle),
      ),
    ].filter(Boolean);
    if (landValues.length) result.set('land', landValues);
  }

  const eqValues = filters.equipmentValue
    .map((e) => stripFilterLabelTitle(e.title))
    .filter(Boolean);
  if (eqValues.length) result.set('ausstattung', eqValues);

  const fo = getFilterObject(preparedFilters);
  const priceFrom = filters.offerTypeValue === OfferType.RENT ? fo.rentPriceFrom : fo.priceFrom;
  const priceTo = filters.offerTypeValue === OfferType.RENT ? fo.rentPriceTo : fo.priceTo;
  if (priceFrom != null) result.set('price_min', [String(priceFrom)]);
  if (priceTo != null) result.set('price_max', [String(priceTo)]);

  return result;
}

/**
 * Stable JSON signature of the housing search request body, excluding pagination fields
 * (`currentPageIndex`, `itemsPerPage`). Used to detect real filter changes vs page/size changes.
 */
export function getListingSearchSignatureWithoutPagination(
  request: SearchListFilters | undefined,
): string | null {
  if (!request) return null;
  const { currentPageIndex: _currentPageIndex, itemsPerPage: _itemsPerPage, ...rest } = request;
  return JSON.stringify(rest);
}

/**
 * Returns true when `requestFilters` (ignoring pagination) differs from the last emitted signature.
 */
export function hasListingSearchFiltersSignatureChanged(
  lastSignatureRef: { current: string | null },
  requestFilters: SearchListFilters | undefined,
): boolean {
  const sig = getListingSearchSignatureWithoutPagination(requestFilters);
  if (sig === null) return false;
  return lastSignatureRef.current !== sig;
}

/**
 * Updates the last-signature ref and pushes `filter_interaction` rows when the listing search body
 * (without pagination) changed. No-op if the signature is unchanged or missing.
 */
export function pushFilterInteractionForHousingListIfSearchChanged(
  lastSignatureRef: { current: string | null },
  requestFilters: SearchListFilters | undefined,
  filters: FiltersType,
  preparedFilters: FiltersType,
  unitList: UnitsMultilevelList | null | undefined,
  searchSource: ObjectSearchSource,
): void {
  const sig = getListingSearchSignatureWithoutPagination(requestFilters);
  if (sig === null) return;
  if (lastSignatureRef.current === sig) return;
  lastSignatureRef.current = sig;
  pushFilterInteractionForHousingListState(filters, preparedFilters, unitList, searchSource);
}

/**
 * Pushes individual `filter_interaction` events by diffing per-group value sets against storage.
 * For each group: fires `remove` for each dropped value, then `add` for each new value.
 * All groups (mandatory + optional) are processed uniformly.
 */
export function pushFilterInteractionForHousingListState(
  filters: FiltersType,
  preparedFilters: FiltersType,
  unitList: UnitsMultilevelList | null | undefined,
  searchSource: ObjectSearchSource,
) {
  const groupValues = buildFilterGroupValues(filters, preparedFilters, unitList);
  const allGroups = new Set([...OPTIONAL_FILTER_GROUPS, 'angebotstyp']);

  for (const group of allGroups) {
    const storageKey = `${FILTER_INTERACTION_STORAGE_PREFIX}${group}`;
    const oldSet = readStoredValues(storageKey);
    const newValues = groupValues.get(group) ?? [];
    const newSet = new Set(newValues);

    const removed = [...oldSet].filter((v) => !newSet.has(v));
    const added = newValues.filter((v) => !oldSet.has(v));

    if (removed.length) {
      pushFilterInteraction({
        filter_group: group,
        filter_value: removed.join(','),
        filter_action: 'remove',
        search_source: searchSource,
      });
    }
    if (added.length) {
      pushFilterInteraction({
        filter_group: group,
        filter_value: added.join(','),
        filter_action: 'add',
        search_source: searchSource,
      });
    }

    if (removed.length || added.length) {
      if (newValues.length) {
        writeFilterInteractionSnapshot(storageKey, JSON.stringify(newValues));
      } else {
        removeFilterInteractionSnapshot(storageKey);
      }
    }
  }
}
